import { createIcons, Trophy as TrophyIcon, Medal, Award, Lock } from 'lucide'

import { TROPHY_DEFINITIONS, TROPHY_ICON_BY_CATEGORY } from '@/data/trophies'
import { apiService } from '@/services/api'

import { initDataManager } from '@/utils/initdata-refresh'
import type {
  InventoryItem,
  PlayerProfile,
  PlayerProfileInventoryMeta,
  PlayerProfileTrophy,
  TrophyDefinition,
} from '@/types'

interface ProfileRequestContext {
  id: string
  telegramId?: string | null
  display?: string
  username?: string
  rank?: number | null
  totalCracked?: number
}

export class PlayerProfileManager {
  private requestToken = 0
  private readonly inventoryLimit = 12
  private currentProfileId: string | null = null
  private inventoryOffset = 0
  private inventoryTotal = 0
  private isInventoryLoading = false
  private selectedTrophyId: string | null = null

  constructor() {
    this.attachEventListeners()
  }

  private attachEventListeners(): void {
    document.addEventListener('click', (event) => {
      const trophyButton = (event.target as HTMLElement).closest<HTMLButtonElement>(
        '[data-trophy-button]'
      )
      if (trophyButton) {
        event.preventDefault()
        this.handleTrophySelection(trophyButton)
        return
      }

      const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
        '[data-profile-inventory-nav]'
      )
      if (!button) return

      if (button.disabled) return

      event.preventDefault()

      if (!this.currentProfileId || this.isInventoryLoading) return

      const direction = button.dataset.profileInventoryNav
      if (direction === 'prev') {
        this.loadInventoryPage(this.inventoryOffset - this.inventoryLimit)
      } else if (direction === 'next') {
        this.loadInventoryPage(this.inventoryOffset + this.inventoryLimit)
      }
    })
  }

  async showProfile(id: string, context: ProfileRequestContext = { id }): Promise<void> {
    this.currentProfileId = id
    this.inventoryOffset = 0
    this.inventoryTotal = 0

    this.renderLoadingState(context)
    await this.fetchProfile(id, { offset: 0, showFullLoading: true })
  }

  private async loadInventoryPage(offset: number): Promise<void> {
    if (!this.currentProfileId) return

    const limit = this.inventoryLimit
    const total = this.inventoryTotal
    const maxOffset = total > 0 ? Math.max(0, total - limit) : 0
    const clampedOffset = Math.max(0, Math.min(offset, maxOffset))

    if (clampedOffset === this.inventoryOffset) return

    await this.fetchProfile(this.currentProfileId, { offset: clampedOffset })
  }

  private async fetchProfile(
    id: string,
    options: { offset?: number; showFullLoading?: boolean } = {}
  ): Promise<void> {
    const { offset = 0, showFullLoading = false } = options

    const token = ++this.requestToken
    this.isInventoryLoading = true

    if (!showFullLoading) {
      this.updateInventoryPagination(undefined, true)
    }

    try {
      const profile = await apiService.getPlayerProfile(id, {
        limit: this.inventoryLimit,
        offset,
        sort: 'rarity',
      })
      if (token !== this.requestToken) return
      this.renderProfile(profile)
    } catch (error) {
      if (token !== this.requestToken) return
      
      // Handle initData errors specifically
      if (initDataManager.isInitDataError(error)) {
        const handled = initDataManager.handleInitDataError(error)
        if (handled) {
          // Show user-friendly error message
          const message = initDataManager.getErrorMessage(error)
          this.renderError(message)
          return
        }
      }
      
      // Handle other errors
      const message = (error as any)?.message || 'Failed to load profile'
      console.error('Profile loading error:', error)
      this.renderError(message)
    } finally {
      if (token === this.requestToken) {
        this.isInventoryLoading = false
      }
    }
  }

  private renderLoadingState(context: ProfileRequestContext): void {
    const loadingEl = document.getElementById('profileLoading')
    const detailsEl = document.getElementById('profileDetails')
    const errorEl = document.getElementById('profileError')

    if (loadingEl) {
      const name = context.display || this.formatPlayerId(context.telegramId ?? context.id)
      loadingEl.textContent = `Loading ${name}...`
      loadingEl.removeAttribute('hidden')
    }
    detailsEl?.setAttribute('hidden', 'true')
    errorEl?.setAttribute('hidden', 'true')

    const displayName = context.display || this.formatPlayerId(context.telegramId ?? context.id)
    this.updateText('profileDisplayName', displayName)
    this.updateUsername(context.username)
    this.updateRank(context.rank)
    this.updateTotalCracked(context.totalCracked ?? null)
    this.updateStreak({ current: null, best: null, last_played: null })
    this.setAvatar(displayName, null)
    this.showTrophiesPlaceholder('Loading trophies…')
    this.showInventoryPlaceholder('Loading fortunes…')
    this.updateInventoryPagination(undefined, true)
  }

  private renderProfile(profile: PlayerProfile): void {
    const loadingEl = document.getElementById('profileLoading')
    const detailsEl = document.getElementById('profileDetails')
    const errorEl = document.getElementById('profileError')

    loadingEl?.setAttribute('hidden', 'true')
    errorEl?.setAttribute('hidden', 'true')
    detailsEl?.removeAttribute('hidden')

    this.currentProfileId = profile.id

    const displayName = profile.display || this.formatPlayerId(profile.telegram_id ?? profile.id)
    const username = profile.username || null

    this.updateText('profileDisplayName', displayName)
    this.updateUsername(username)
    this.updateRank(profile.rank)
    this.updateTotalCracked(profile.total_cracked)
    this.updateStreak(profile.streak)
    this.setAvatar(displayName, profile.photo_url)

    this.renderTrophies(profile.trophies)

    const inventory = this.normalizeInventory(profile)
    this.renderInventoryItems(inventory.items, inventory.meta)
  }

  private renderError(message: string): void {
    const loadingEl = document.getElementById('profileLoading')
    const detailsEl = document.getElementById('profileDetails')
    const errorEl = document.getElementById('profileError')

    loadingEl?.setAttribute('hidden', 'true')
    const detailsVisible = detailsEl ? !detailsEl.hasAttribute('hidden') : false
    if (!detailsVisible) {
      detailsEl?.setAttribute('hidden', 'true')
      this.showInventoryPlaceholder('Unable to load fortunes')
      this.showTrophiesPlaceholder('Unable to load trophies')
      this.updateInventoryPagination({ total: 0, limit: this.inventoryLimit, offset: 0 })
    } else {
      this.updateInventoryPagination(
        { total: this.inventoryTotal, limit: this.inventoryLimit, offset: this.inventoryOffset }
      )
    }
    if (errorEl) {
      errorEl.textContent = message || 'Failed to load profile'
      errorEl.removeAttribute('hidden')
    }
  }

  private updateText(id: string, value: string): void {
    const el = document.getElementById(id)
    if (!el) return
    el.textContent = value
  }

  private updateUsername(username: string | null | undefined): void {
    const usernameEl = document.getElementById('profileUsername') as HTMLAnchorElement | null
    if (!usernameEl) return

    if (username) {
      const clean = username.startsWith('@') ? username : `@${username}`
      usernameEl.textContent = clean
      usernameEl.href = `https://t.me/${clean.replace('@', '')}`
      usernameEl.style.display = 'inline-flex'
    } else {
      usernameEl.textContent = ''
      usernameEl.removeAttribute('href')
      usernameEl.style.display = 'none'
    }
  }

  private updateRank(rank: number | null | undefined): void {
    this.updateText('profileRank', rank ? `#${rank}` : '#-')
  }

  private updateTotalCracked(total: number | null): void {
    if (total == null) {
      this.updateText('profileTotalCracked', '—')
      return
    }
    this.updateText('profileTotalCracked', total.toString())
  }

  private updateStreak(streak: { current: number | null; best: number | null; last_played: string | null }): void {
    const current = streak.current ?? 0
    const best = streak.best ?? 0
    this.updateText('profileStreakCurrent', `${current}`)
    this.updateText('profileStreakBest', `${best}`)
  }

  private setAvatar(displayName: string, photoUrl?: string | null): void {
    const avatarImg = document.getElementById('profileAvatarImage') as HTMLImageElement | null
    const avatarInitials = document.getElementById('profileAvatarInitials')
    if (!avatarImg || !avatarInitials) return

    // Set initials as fallback
    avatarInitials.textContent = this.getInitials(displayName)
    avatarInitials.classList.remove('hidden')
    avatarImg.classList.add('hidden')
    avatarImg.removeAttribute('src')

    // Try to use photo_url from initData if available
    if (photoUrl) {
      console.log('Using photo_url from initData:', photoUrl)
      
      avatarImg.onload = () => {
        console.log('Profile photo loaded successfully')
        avatarInitials.classList.add('hidden')
        avatarImg.classList.remove('hidden')
      }
      
      avatarImg.onerror = (error) => {
        console.log('Profile photo failed to load, using initials:', error)
        avatarImg.classList.add('hidden')
        avatarInitials.classList.remove('hidden')
      }
      
      avatarImg.src = photoUrl
    }
  }

  private renderTrophies(trophies?: PlayerProfileTrophy[] | null): void {
    const container = document.getElementById('profileTrophiesList')
    const detailEl = document.getElementById('profileTrophyDetail')
    if (!container || !detailEl) return

    const activeTrophies = this.normalizeTrophies(trophies)
    const activeIds = new Set(activeTrophies.map(trophy => trophy.id))

    if (TROPHY_DEFINITIONS.length === 0) {
      this.showTrophiesPlaceholder('No trophies yet')
      return
    }

    const html = TROPHY_DEFINITIONS.map(trophy => {
      const unlocked = activeIds.has(trophy.id)
      const iconName = this.getTrophyIconName(trophy)
      const isSelected = this.selectedTrophyId === trophy.id
      const statusLabel = unlocked ? 'Unlocked' : 'Locked'

      return `
        <button
          type="button"
          class="trophy-badge ${unlocked ? 'is-unlocked' : 'is-locked'}${
            isSelected ? ' is-selected' : ''
          }"
          data-trophy-button
          data-trophy-id="${this.escapeHtmlAttr(trophy.id)}"
          data-trophy-label="${this.escapeHtmlAttr(trophy.label)}"
          data-trophy-description="${this.escapeHtmlAttr(trophy.description)}"
          data-trophy-icon="${this.escapeHtmlAttr(iconName)}"
          data-trophy-unlocked="${unlocked ? 'true' : 'false'}"
          title="${this.escapeHtmlAttr(trophy.label)}"
          aria-pressed="${isSelected}"
          aria-label="${this.escapeHtmlAttr(`${trophy.label} — ${statusLabel}`)}"
          role="listitem"
        >
          <span class="trophy-badge-icon"><i data-lucide="${iconName}"></i></span>
        </button>
      `
    }).join('')

    container.innerHTML = html
    detailEl.classList.remove('empty')

    createIcons({
      icons: {
        Trophy: TrophyIcon,
        Medal,
        Award,
        Lock,
      },
    })

    const defaultTrophyId = this.resolveDefaultTrophySelection(activeIds)
    this.selectTrophy(defaultTrophyId)
  }

  private showTrophiesPlaceholder(message: string): void {
    const container = document.getElementById('profileTrophiesList')
    const detailEl = document.getElementById('profileTrophyDetail')
    if (container) {
      container.innerHTML = ''
    }
    if (detailEl) {
      detailEl.classList.add('empty')
      detailEl.classList.remove('is-unlocked', 'is-locked')
      detailEl.textContent = message
    }
    this.selectedTrophyId = null
  }

  private getTrophyIconName(trophy: TrophyDefinition): string {
    return TROPHY_ICON_BY_CATEGORY[trophy.category] ?? 'award'
  }

  private resolveDefaultTrophySelection(activeIds: Set<string>): string | null {
    if (this.selectedTrophyId && activeIds.has(this.selectedTrophyId)) {
      return this.selectedTrophyId
    }

    if (this.selectedTrophyId && TROPHY_DEFINITIONS.some(trophy => trophy.id === this.selectedTrophyId)) {
      return this.selectedTrophyId
    }

    const firstUnlocked = TROPHY_DEFINITIONS.find(trophy => activeIds.has(trophy.id))
    if (firstUnlocked) {
      return firstUnlocked.id
    }

    return TROPHY_DEFINITIONS[0]?.id ?? null
  }

  private normalizeTrophies(trophies?: PlayerProfileTrophy[] | null): TrophyDefinition[] {
    if (!trophies || trophies.length === 0) return []

    return trophies
      .map(trophy => {
        if (!trophy) return null
        if (typeof trophy === 'string') {
          return TROPHY_DEFINITIONS.find(def => def.id === trophy) ?? null
        }
        if (typeof trophy === 'object' && 'id' in trophy) {
          const def = TROPHY_DEFINITIONS.find(definition => definition.id === trophy.id)
          return {
            id: trophy.id,
            label: trophy.label ?? def?.label ?? trophy.id,
            description: trophy.description ?? def?.description ?? '',
            requirements: trophy.requirements ?? def?.requirements ?? {},
            category: trophy.category ?? def?.category ?? 'rarity_hold',
          }
        }
        return null
      })
      .filter((value): value is TrophyDefinition => value !== null)
  }

  private handleTrophySelection(button: HTMLButtonElement): void {
    const trophyId = button.dataset.trophyId ?? null
    this.selectTrophy(trophyId)
  }

  private selectTrophy(trophyId: string | null): void {
    const container = document.getElementById('profileTrophiesList')
    const detailEl = document.getElementById('profileTrophyDetail')
    if (!container || !detailEl) return

    const buttons = Array.from(
      container.querySelectorAll<HTMLButtonElement>('[data-trophy-button]')
    )

    if (buttons.length === 0) {
      detailEl.classList.add('empty')
      detailEl.classList.remove('is-unlocked', 'is-locked')
      detailEl.textContent = 'No trophies yet'
      this.selectedTrophyId = null
      return
    }

    let target = trophyId
      ? buttons.find(button => button.dataset.trophyId === trophyId) ?? null
      : null

    if (!target) {
      target = buttons[0] ?? null
    }

    buttons.forEach(button => {
      const isSelected = button === target
      button.classList.toggle('is-selected', isSelected)
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false')
    })

    if (!target) {
      detailEl.classList.add('empty')
      detailEl.classList.remove('is-unlocked', 'is-locked')
      detailEl.textContent = 'No trophies yet'
      this.selectedTrophyId = null
      return
    }

    this.selectedTrophyId = target.dataset.trophyId ?? null
    this.updateTrophyDetailFromButton(target)
  }

  private updateTrophyDetailFromButton(button: HTMLButtonElement): void {
    const detailEl = document.getElementById('profileTrophyDetail')
    if (!detailEl) return

    const label = button.dataset.trophyLabel ?? ''
    const description = button.dataset.trophyDescription ?? ''
    const iconName = button.dataset.trophyIcon ?? 'award'
    const unlocked = button.dataset.trophyUnlocked === 'true'
    const statusText = unlocked ? 'Unlocked' : 'How to unlock'

    detailEl.classList.remove('empty')
    detailEl.classList.toggle('is-unlocked', unlocked)
    detailEl.classList.toggle('is-locked', !unlocked)
    detailEl.innerHTML = `
      <div class="trophy-detail-header">
        <span class="trophy-detail-icon ${unlocked ? 'is-unlocked' : 'is-locked'}">
          <i data-lucide="${this.escapeHtml(iconName)}"></i>
        </span>
        <div class="trophy-detail-text">
          <span class="trophy-detail-label">${this.escapeHtml(label)}</span>
          <span class="trophy-detail-status">${statusText}</span>
        </div>
      </div>
      <p class="trophy-detail-description">${this.escapeHtml(description)}</p>
    `

    createIcons({
      icons: {
        Trophy: TrophyIcon,
        Medal,
        Award,
        Lock,
      },
    })
  }

  private normalizeInventory(profile: PlayerProfile): { items: InventoryItem[]; meta?: PlayerProfileInventoryMeta } {
    const inventory = profile.fortune_inventory as unknown
    if (Array.isArray(inventory)) {
      return { items: inventory as InventoryItem[] }
    }
    if (inventory && typeof inventory === 'object' && 'items' in (inventory as Record<string, unknown>)) {
      const data = inventory as { items: InventoryItem[]; meta?: PlayerProfileInventoryMeta }
      return { items: data.items ?? [], meta: data.meta }
    }
    return { items: [] }
  }

  private renderInventoryItems(
    items: InventoryItem[],
    meta?: PlayerProfileInventoryMeta
  ): void {
    const container = document.getElementById('profileInventoryList')
    if (!container) return

    if (!items || items.length === 0) {
      container.innerHTML = '<div class="empty-inventory">No fortunes yet</div>'
      const fallbackMeta: PlayerProfileInventoryMeta = {
        total: meta?.total ?? 0,
        limit: meta?.limit ?? this.inventoryLimit,
        offset: meta?.offset ?? 0,
      }
      this.updateInventoryPagination(fallbackMeta)
      return
    }

    const html = items.map(item => this.renderInventoryItem(item)).join('')
    container.innerHTML = html

    const normalizedMeta: PlayerProfileInventoryMeta = {
      total: meta?.total ?? items.length,
      limit: meta?.limit ?? this.inventoryLimit,
      offset: meta?.offset ?? 0,
    }
    this.updateInventoryPagination(normalizedMeta)
  }

  private renderInventoryItem(item: InventoryItem): string {
    const rarity = (item.rarity_tier || 'common').toLowerCase()
    const rarityClass = `rarity-${rarity}`
    const rarityBorderClass = `rarity-border-${rarity}`
    const stars = '★'.repeat(Math.max(0, item.stars ?? 0))
    const starLabel = stars ? ` ${this.escapeHtml(stars)}` : ''

    const message = item.message?.trim() ? item.message : 'No message'
    const quotedMessage = `&ldquo;${this.escapeHtml(message)}&rdquo;`

    return `
      <div class="fortune-card fortune-card--profile ${rarityBorderClass}">
        <div class="fortune-card-header">
          <span class="rarity-badge ${rarityClass}">
            ${this.escapeHtml((item.rarity_tier || 'FORTUNE').toUpperCase())}${starLabel}
          </span>
        </div>
        <div class="fortune-card-body">
          <p class="fortune-message">${quotedMessage}</p>
        </div>
      </div>
    `
  }

  private showInventoryPlaceholder(message: string): void {
    const container = document.getElementById('profileInventoryList')
    if (!container) return
    container.innerHTML = `<div class="empty-inventory">${this.escapeHtml(message)}</div>`
  }

  private updateInventoryPagination(
    meta?: PlayerProfileInventoryMeta,
    loading: boolean = false
  ): void {
    const prevBtn = document.getElementById('profileInventoryPrev') as HTMLButtonElement | null
    const nextBtn = document.getElementById('profileInventoryNext') as HTMLButtonElement | null
    const pageInfo = document.getElementById('profileInventoryPageInfo')

    if (loading) {
      if (prevBtn) prevBtn.disabled = true
      if (nextBtn) nextBtn.disabled = true
      if (pageInfo) pageInfo.textContent = 'Loading…'
      return
    }

    const limit = meta?.limit ?? this.inventoryLimit
    const offset = meta?.offset ?? 0
    const total = meta?.total ?? 0

    this.inventoryOffset = offset
    this.inventoryTotal = total

    if (pageInfo) {
      if (total <= 0) {
        pageInfo.textContent = '0-0 of 0'
      } else {
        const start = offset + 1
        const end = Math.min(offset + limit, total)
        pageInfo.textContent = `${start}-${end} of ${total}`
      }
    }

    const isAtStart = offset <= 0 || total === 0
    const isAtEnd = total === 0 || offset + limit >= total

    if (prevBtn) prevBtn.disabled = isAtStart
    if (nextBtn) nextBtn.disabled = isAtEnd
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  private escapeHtmlAttr(value: string): string {
    return this.escapeHtml(String(value)).replace(/`/g, '&#96;')
  }

  private getInitials(name: string): string {
    const parts = name.trim().split(/\s+/)
    if (parts.length === 0) return '?'
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
  }

  private formatPlayerId(id: string): string {
    if (id.length <= 10) return id
    return `${id.slice(0, 6)}...${id.slice(-4)}`
  }
}
